import JSBI from 'jsbi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

// import BasicTable, { Column } from '#/components/basic/table/BasicTable';
// import TextColored from '#/components/basic/text/TextColored';
// import ToggleIcon from '#/components/basic/Button/ToggleIcon';
// import { formatNumber, formatApiNumber } from '#/lib/display/formatting';
// import { toggleFavoriteMarket } from '#/reduxState/userSettings/userSettingsReducer';
import { calculateLeverage } from '#/api/apiUtils';
import {
  StateTicker,
  StateTickersMap,
  useTickersQuery,
} from '#/api/queries/tickersQueries';
import Button from '#/components/basic/Button/Button';
import ButtonRouterLink from '#/components/basic/Button/ButtonRouterLink';
import BasicTableStackable from '#/components/basic/table/BasicTableStackable';
import TextColored from '#/components/basic/text/TextColored';
import { useActiveWindowInterval } from '#/components/hooks';
import { getMarketTradingHoursLabel } from '#/components/markets/intermittent/intermittentMarkets';
import MarginTypeChip from '#/components/markets/intermittent/MarginTypeChip';
import MarketSessionBadge from '#/components/markets/intermittent/MarketSessionBadge';
import { CoinIcon } from '#/config/coinIcons/CoinIcon';
import {
  abbreviateNumber,
  formatNumber,
  formatUsdWithSign,
} from '#/lib/display/formatting';
import { filterEmpty } from '#/lib/utils';
import { useSelector } from '#/reduxState/store';

const TableWrapper = styled.div`
  font-size: var(--smaller);
`;

const TableWrapperMobile = styled(TableWrapper)`
  padding: 10px;
`;

const Text = styled.span<{ $highlight: boolean }>`
  color: ${({ $highlight }) => ($highlight ? 'var(--white)' : 'var(--gray4c)')};
`;

// Badge: purple for isolated markets, default gray otherwise
const Bubble = styled.span<{ $isolated?: boolean }>`
  display: inline-block;
  color: var(--white);
  text-align: center;
  font-family: var(--standardFontFamily);
  font-size: var(--smallest);
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;

  border-radius: 2px;
  background: ${({ $isolated }) =>
    $isolated ? 'var(--purple6)' : 'var(--gray2a)'};
  padding: 0 4px;
  margin-left: 4px;
`;

/* Centers coin icon, market name and badges on one vertical axis.
   $multiline lets the session badge wrap onto its own row (mobile) */
const MarketCell = styled.div<{ $marginLeft: number; $multiline?: boolean }>`
  display: flex;
  align-items: center;
  margin-left: ${({ $marginLeft }) => $marginLeft}px;
  ${({ $multiline }) => ($multiline ? 'flex-wrap: wrap;' : '')}
`;

/* Mobile: session badge on its own line, starting at the beginning of the cell */
const SessionBadgeRow = styled.div`
  flex-basis: 100%;
  margin-top: 2px;

  & > * {
    margin-left: 0;
  }

  /* No extra height on rows without a session badge */
  &:empty {
    display: none;
  }
`;

type SortColumn =
  | 'symbol'
  | 'volume'
  | 'price'
  | 'change'
  | 'openInterestInQuote';

function sortTickers(
  sortedTickersMarkets: null | string[],
  tickersMap: StateTickersMap,
  marketsPinPosition: { [market: string]: number },
  sortBy: SortColumn,
  sortDescending: boolean
): string[] {
  // To impmrove perf we are using already pre-sorted list when possible.
  // It makes re-sorting super-fast after websocket updates are received in realtime.
  const preSortedList = sortedTickersMarkets
    ? sortedTickersMarkets
    : Object.values(tickersMap).map(ticker => ticker.market);
  const sortMultiplier = sortDescending ? 1 : -1;

  return (
    preSortedList
      // Filter out items removed from ticker map
      .filter(market => !!tickersMap[market])
      .sort((aMarket, bMarket) => {
        const aPinPosition = marketsPinPosition[aMarket];
        const bPinPosition = marketsPinPosition[bMarket];

        // We need to sort by pin as priority.
        // For equal pin, we use sort column for order resolution
        if ((aPinPosition || bPinPosition) && aPinPosition !== bPinPosition) {
          return !bPinPosition
            ? -1
            : !aPinPosition
            ? 1
            : aPinPosition < bPinPosition
            ? -1
            : 1;
        }

        const aTicker = tickersMap[aMarket];
        const bTicker = tickersMap[bMarket];
        if (sortBy === 'volume') {
          return JSBI.greaterThan(
            aTicker.quoteVolumePip,
            bTicker.quoteVolumePip
          )
            ? sortMultiplier * -1
            : JSBI.lessThan(aTicker.quoteVolumePip, bTicker.quoteVolumePip)
            ? sortMultiplier * 1
            : aTicker.market.localeCompare(bTicker.market);
        }
        if (sortBy === 'symbol') {
          return (
            sortMultiplier *
            bTicker.baseSymbol.localeCompare(aTicker.baseSymbol)
          );
        }
        if (sortBy === 'change') {
          if (aTicker.percentChange === bTicker.percentChange) {
            return aTicker.baseSymbol.localeCompare(bTicker.baseSymbol);
          }
          return (
            sortMultiplier *
            (Number(bTicker.percentChange) - Number(aTicker.percentChange))
          );
        }
        if (sortBy === 'price') {
          if (aTicker.close === bTicker.close) {
            return aTicker.baseSymbol.localeCompare(bTicker.baseSymbol);
          }
          return (
            sortMultiplier * (Number(bTicker.close) - Number(aTicker.close))
          );
        }
        if (sortBy === 'openInterestInQuote') {
          if (aTicker.openInterestInQuote === bTicker.openInterestInQuote) {
            return aTicker.baseSymbol.localeCompare(bTicker.baseSymbol);
          }
          return (
            sortMultiplier *
            (Number(bTicker.openInterestInQuote || 0) -
              Number(aTicker.openInterestInQuote || 0))
          );
        }
        return 0;
      })
  );
}

type Props = {
  /** Use for navigation with side effects (eg. closing sidebar) */
  onMarketClick?: (marketSymbol: string) => void;
  /** Use for pure navigation - allows opening market in a new tab */
  marketRouterLink?: (marketSymbol: string) => string;
  isSidebar?: boolean;
  // placement: 'page' | 'sidebar'
};

function MarketsTable({ onMarketClick, marketRouterLink, isSidebar }: Props) {
  const isMobile = useSelector(state => state.screen.isMobile);
  const currentMarketPair = useSelector(
    state => state.markets.currentMarket.pair
  );
  const marketsPinPosition = useSelector(
    state => state.markets.listedMarkets.marketsPinPosition
  );
  const marketsLeverageParams = useSelector(
    state => state.markets.listedMarkets.marketsLeverageParams
  );
  const marketsTradingInfo = useSelector(
    state => state.markets.listedMarkets.marketsTradingInfo
  );

  const [sort, setSort] = useState<{
    byColumn: SortColumn;
    descending: boolean;
  }>({
    byColumn: 'volume',
    descending: true,
  });

  const { data: tickersMap, dataUpdatedAt } = useTickersQuery();

  const Wrapper = isMobile ? TableWrapperMobile : TableWrapper;

  const tickSizes = useSelector(state => state.markets.listedMarkets.tickSizes);
  const [immediateResortRequired, setImmediateResortRequired] = useState(false);
  const [resortRequired, setResortRequired] = useState(false);
  const [sortedTickersMarkets, setSortedTickersMarkets] = useState<
    null | string[]
  >(null);

  const sortAndSetTickers = useCallback(() => {
    setResortRequired(false);

    if (!marketsPinPosition) {
      console.log('Markets pin positions not loaded yet');
      return;
    }
    if (!dataUpdatedAt) {
      return;
    }
    const sorted = sortTickers(
      sortedTickersMarkets,
      tickersMap || {},
      marketsPinPosition,
      sort.byColumn,
      sort.descending
    );
    setSortedTickersMarkets(sorted);
  }, [
    dataUpdatedAt,
    marketsPinPosition,
    sort.byColumn,
    sort.descending,
    sortedTickersMarkets,
    tickersMap,
  ]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setResortRequired(true);
  }, [marketsPinPosition, tickersMap]);

  // For regular websocket updates, we just re-sort every 2 seconds
  useActiveWindowInterval(() => {
    if (resortRequired) {
      sortAndSetTickers();
    }
  }, 2000);

  // For sorting change, we need to update immediatelly
  useEffect(() => {
    if (!immediateResortRequired) {
      return;
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setImmediateResortRequired(false);
    sortAndSetTickers();
  }, [immediateResortRequired, sortAndSetTickers]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setImmediateResortRequired(true);
  }, [dataUpdatedAt, sort.byColumn, sort.descending]);

  const handleSort = (column: SortColumn) => {
    setSort({
      byColumn: column,
      descending: sort.byColumn === column ? !sort.descending : true,
    });
  };

  const items = sortedTickersMarkets
    ?.map(market => tickersMap?.[market])
    .filter(filterEmpty) as StateTicker[];
  /** If there are pinned items, this is the one with highest position */
  const lastPinnedItem = useMemo(() => {
    if (!marketsPinPosition || !items) {
      return;
    }
    const firstNonPinned = items?.findIndex(
      // TODO: pinned positions are now "featured" flag on markets response
      ticker => !marketsPinPosition[ticker.market]
    );
    if (firstNonPinned > 0) {
      return items[firstNonPinned - 1];
    }
  }, [items, marketsPinPosition]);

  if (items && items.length === 0) {
    // No search results
    return null;
  }

  if (!items) {
    return <div style={{ minHeight: 350 }} />;
  }

  return (
    <Wrapper>
      <BasicTableStackable
        rowsVerticalPadding={isSidebar ? 8 : 11}
        items={items || []}
        rowCustomizer={item => ({
          selected:
            (isSidebar || isMobile) && item.market === currentMarketPair,
          underlined: !!lastPinnedItem && item.market === lastPinnedItem.market,
        })}
        getItemKey={item => item.market}
        rowRouterLink={
          (isSidebar || isMobile) && marketRouterLink
            ? item => marketRouterLink(item.market)
            : undefined
        }
        onRowClick={
          (isSidebar || isMobile) && onMarketClick
            ? item => onMarketClick(item.market)
            : undefined
        }
        watchValueAndAnimateOnChange={item => item.quoteVolume}
        // TODO: remove stripes option ?
        stripes={isMobile || !isSidebar}
        notFixedLayout
        marginBottom="0px"
        columns={item => {
          const marketLeverageParams = item?.market
            ? marketsLeverageParams?.[item.market]
            : undefined;
          const marketLeverage = marketLeverageParams
            ? calculateLeverage(marketLeverageParams?.initialMarginFraction)
            : '';
          const marketSessionInfo = item?.market
            ? marketsTradingInfo?.[item.market]
            : undefined;
          const tradingHoursLabel = getMarketTradingHoursLabel(
            marketSessionInfo?.type
          );
          return [
            {
              name: 'Market',
              padding: '0 0 0 16px',
              sorted:
                sort.byColumn === 'symbol' ? (sort.descending ? 1 : -1) : 0,
              onClick: () => handleSort('symbol'),
              fontFamily: 'standard',
              render: (
                <MarketCell
                  $marginLeft={isMobile ? 8 : 16}
                  $multiline={isMobile}
                >
                  {item?.baseSymbol && (
                    <CoinIcon
                      margin="0 5px 0 0"
                      symbol={item.baseSymbol}
                      size={isSidebar || isMobile ? '16px' : '32px'}
                    />
                  )}
                  <Text $highlight={true}>{item?.baseSymbol}</Text>
                  <Text $highlight={false}>-{item?.quoteSymbol}</Text>
                  {marketLeverage && (
                    <Bubble
                      $isolated={marketSessionInfo?.marginType === 'isolated'}
                    >
                      {marketLeverage}x
                    </Bubble>
                  )}
                  {tradingHoursLabel && (
                    <Bubble
                      $isolated={marketSessionInfo?.marginType === 'isolated'}
                    >
                      {tradingHoursLabel}
                    </Bubble>
                  )}
                  {isMobile ? (
                    <SessionBadgeRow>
                      <MarketSessionBadge sessionInfo={marketSessionInfo} />
                    </SessionBadgeRow>
                  ) : (
                    <MarketSessionBadge sessionInfo={marketSessionInfo} />
                  )}
                </MarketCell>
              ),
            },
            {
              name: 'Type',
              fontFamily: 'standard',
              align: 'right',
              render: isMobile
                ? false
                : marketSessionInfo && (
                    <MarginTypeChip marginType={marketSessionInfo.marginType} />
                  ),
            },
            {
              name: 'Price',
              sorted:
                sort.byColumn === 'price' ? (sort.descending ? 1 : -1) : 0,
              onClick: () => handleSort('price'),
              align: isMobile || isSidebar ? 'right' : 'left',
              render: item => (
                <>
                  {item.close &&
                    formatNumber(item.close, {
                      fixedDecimals: tickSizes[item.market]?.visibleDecimals,
                    })}
                </>
              ),
            },
            {
              name: '24h Change',
              stackWithPrevious: isMobile,
              align: isMobile || isSidebar ? 'right' : 'left',
              sorted:
                sort.byColumn === 'change' ? (sort.descending ? 1 : -1) : 0,
              onClick: () => handleSort('change'),
              render: (
                <>
                  {item && (
                    <TextColored
                      color={Number(item.percentChange) >= 0 ? 'green' : 'red'}
                    >
                      {Number(item.percentChange) >= 0 && '+'}
                      {Number(item.percentChange).toFixed(2)}%
                    </TextColored>
                  )}
                </>
              ),
            },
            {
              name: '8h Funding',
              align: isMobile || isSidebar ? 'right' : 'left',
              // TODO: Markets funding sort support
              // sorted:
              //   sort.byColumn === 'funding8h' ? (sort.descending ? 1 : -1) : 0,
              // onClick: () => handleSort('funding8h'),
              render: item => (
                <>
                  {item.currentFundingRate
                    ? `${formatNumber(Number(item.currentFundingRate) * 100, {
                        fixedDecimals: 4,
                      })}%`
                    : '-'}
                </>
              ),
            },
            {
              name: '24h High/Low',
              render:
                isMobile || isSidebar
                  ? false
                  : item => (
                      <>
                        {formatUsdWithSign(item.high || 0)} /{' '}
                        {formatUsdWithSign(item.low || 0)}
                      </>
                    ),
            },
            {
              name: '24h Volume',
              stackWithPrevious: isMobile,
              sorted:
                sort.byColumn === 'volume' ? (sort.descending ? 1 : -1) : 0,
              align: isMobile || isSidebar ? 'right' : 'left',
              onClick: () => handleSort('volume'),
              render: item => (
                <>${abbreviateNumber(Number(item.quoteVolume), 2)}</>
              ),
            },
            {
              name: 'Open Interest',
              sorted:
                sort.byColumn === 'openInterestInQuote'
                  ? sort.descending
                    ? 1
                    : -1
                  : 0,
              align: isSidebar ? 'right' : 'left',
              onClick: () => handleSort('openInterestInQuote'),
              render: isMobile
                ? false
                : item =>
                    item.openInterest
                      ? `$${abbreviateNumber(item.openInterestInQuote, 2)}`
                      : '-',
            },
            {
              name: '',
              fontFamily: 'standard',
              render:
                isMobile || isSidebar ? (
                  false
                ) : !item?.market ? null : marketRouterLink ? (
                  <ButtonRouterLink to={marketRouterLink(item.market)} bordered>
                    Trade
                  </ButtonRouterLink>
                ) : (
                  <Button onClick={() => onMarketClick?.(item.market)} bordered>
                    Trade
                  </Button>
                ),
            },
          ];
        }}
      />
    </Wrapper>
  );
}

export default MarketsTable;
